import React from 'react';
import {
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Tooltip,
  Box,
  FormHelperText,
  Typography,
  GridProps,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSchemaContext } from './SparkForm';
import { useField } from 'formik';
import { DropdownOption } from 'types/common/DropdownOption';

type DisabledEntity = { value: string; helperComponent?: React.ReactNode };

type DropdownOptionsWithHeaderDescription = DropdownOption & {
  headerDescription?: string | null;
};

type SparkRadioGroupProps = {
  name: string;
  helpText?: string;
  label?: string | React.ReactNode;
  options: DropdownOptionsWithHeaderDescription[];
  onChange?: (value: string) => void;
  disabledEntities?: DisabledEntity[];
  subHeaderText?: string;
  size?: GridProps['xs'];
  disableField?: boolean;
};

function SparkRadioGroup({
  name,
  helpText,
  label,
  options,
  onChange,
  disabledEntities,
  subHeaderText = '',
  size = 12,
  disableField = false,
}: SparkRadioGroupProps) {
  const [field, meta, helper] = useField<string>(name);
  const { validationSchema } = useSchemaContext();
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const getDisbaledState = (value: string | number) => {
    const foundEntity = disabledEntities?.find(
      (entity) => entity.value === value
    );
    if (!foundEntity) {
      return;
    }
    return foundEntity;
  };

  const handleChange = (value: string) => {
    helper.setValue(value);
    onChange && onChange(value);
  };
  const isError = meta.touched && !!meta.error;
  return (
    <Grid item xs={size} display={'flex'} flexDirection='column'>
      {!!label && (
        <InputLabel
          sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
        >
          <span>{isFieldRequired && '*'}</span>
          {label}
          {helpText && (
            <Tooltip title={helpText} placement='top' arrow>
              <InfoOutlinedIcon fontSize='small' />
            </Tooltip>
          )}
        </InputLabel>
      )}
      {subHeaderText && (
        <Typography
          color='#A3A3A3'
          marginLeft={1.6}
          fontSize={12}
          textAlign='left'
        >
          {subHeaderText}
        </Typography>
      )}
      <RadioGroup
        aria-label={name}
        {...field}
        onChange={(_, value) => handleChange(value)}
        sx={{ padding: 2 }}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            sx={{ alignItems: 'flex-start' }}
            disabled={!!getDisbaledState(option.value) || disableField}
            label={
              <Box paddingTop={1}>
                <InputLabel
                  sx={{
                    display: 'flex',
                    gap: 1,
                    fontSize: 16,
                  }}
                >
                  {option.label}
                  {getDisbaledState(option.value)?.helperComponent || <></>}
                  {option.headerDescription ? (
                    <Chip
                      size='small'
                      sx={{
                        color: 'white',
                      }}
                      label={option.headerDescription}
                      onClick={() => {}}
                    />
                  ) : (
                    <></>
                  )}
                </InputLabel>
                {!!option.description && (
                  <InputLabel
                    sx={{
                      fontSize: 14,
                      fontFamily: 'Mont-semibold',
                      color: '#A3A3A3',
                    }}
                  >
                    {option.description}
                  </InputLabel>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : <></>}
      </FormHelperText>
    </Grid>
  );
}

export default SparkRadioGroup;
